import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

/**
 * Reusable "Change my password" card — drop into Settings, profile pages, or anywhere logged-in users live.
 * Calls POST /api/auth/change-password.
 */
export default function ChangePasswordCard({ compact = false, testId = "change-password" }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== conf) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext(""); setConf("");
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className={`border border-border ${compact ? "p-5" : "p-8"} space-y-4`} data-testid={`${testId}-card`}>
      <div className="flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-primary" />
        <div>
          <h2 className={`font-display ${compact ? "text-lg" : "text-xl"} font-bold tracking-tight`}>Change my password</h2>
          <p className="text-xs text-muted-foreground">Choose a new password — minimum 6 characters.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label htmlFor={`${testId}-current`}>Current password</Label>
          <PasswordInput id={`${testId}-current`} required value={cur} onChange={(e) => setCur(e.target.value)}
                 className="rounded-sm h-10" autoComplete="current-password" inputTestId={`${testId}-current`} testId={`${testId}-current-toggle`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-new`}>New password</Label>
          <PasswordInput id={`${testId}-new`} required value={next} onChange={(e) => setNext(e.target.value)}
                 className="rounded-sm h-10" minLength={6} autoComplete="new-password" inputTestId={`${testId}-new`} testId={`${testId}-new-toggle`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-confirm`}>Confirm</Label>
          <PasswordInput id={`${testId}-confirm`} required value={conf} onChange={(e) => setConf(e.target.value)}
                 className="rounded-sm h-10" minLength={6} autoComplete="new-password" inputTestId={`${testId}-confirm`} testId={`${testId}-confirm-toggle`} />
        </div>
      </div>
      <div>
        <Button type="submit" disabled={busy} className="rounded-sm" data-testid={`${testId}-submit`}>
          {busy ? "Updating…" : "Update password"}
        </Button>
      </div>
    </form>
  );
}
